// ============================================================================
// GST TURNOVER SERVICE (ADR-0021 §4)
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { gstEndpoints } from '../endpoints/gst.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { PaginatedResponse } from '../types';
import type {
  GstTurnover,
  GstTurnoverSearchRequest,
  CreateGstTurnoverRequest,
  UpdateGstTurnoverRequest,
  GstApplicability,
} from '../types/gstTurnover.types';

class GstTurnoverService {
  async getAll(params?: GstTurnoverSearchRequest): Promise<PaginatedResponse<GstTurnover>> {
    const endpoint = params
      ? buildEndpointWithQuery(gstEndpoints.turnoverBasePath(), params as Record<string, any>)
      : gstEndpoints.turnover();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const paged = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: paged.items ?? [],
      pagination: {
        currentPage: paged.page ?? 1,
        pageSize: paged.pageSize ?? 25,
        totalPages: paged.totalPages ?? 0,
        totalItems: paged.totalCount ?? 0,
        hasNextPage: paged.hasNext ?? false,
        hasPreviousPage: paged.hasPrevious ?? false,
      },
    };
  }

  /**
   * Every year recorded for one client, newest first — the whole history,
   * because e-invoice applicability looks back over all of it.
   */
  async getByClient(clientId: string): Promise<GstTurnover[]> {
    const response = await get<any>(gstEndpoints.turnoverByClient(clientId));
    return (response.data?.data ?? []) as GstTurnover[];
  }

  /**
   * What the recorded turnover implies for a financial year. Every flag is
   * advisory and carries its own explanation; `applicable` is null where
   * nothing is known, which is not the same as false.
   */
  async getApplicability(clientId: string, financialYear: number): Promise<GstApplicability> {
    const endpoint = buildEndpointWithQuery(
      gstEndpoints.turnoverApplicabilityPath(clientId), { financialYear });
    const response = await get<any>(endpoint);
    return response.data?.data as GstApplicability;
  }

  async create(request: CreateGstTurnoverRequest): Promise<GstTurnover> {
    const response = await post<any>(gstEndpoints.turnover(), request);
    return response.data?.data as GstTurnover;
  }

  async update(id: string, request: UpdateGstTurnoverRequest): Promise<GstTurnover> {
    const response = await put<any>(gstEndpoints.turnoverById(id), request);
    return response.data?.data as GstTurnover;
  }

  async remove(id: string): Promise<void> {
    await del<any>(gstEndpoints.turnoverById(id));
  }
}

export const gstTurnoverService = new GstTurnoverService();
export default gstTurnoverService;
